/**
 * Hook React pour la gestion 2FA
 * RÉUTILISABILITÉ : 100% - Compatible Next.js et React
 *
 * Ce hook encapsule la logique d'authentification à deux facteurs :
 * - Génération de QR code TOTP
 * - Envoi de codes par email
 * - Activation/Désactivation 2FA
 * - Gestion des codes de backup
 */

  // Client Component pour Next.js

import { useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { twoFactorApi } from '../../infrastructure/api';
import type { ApiResponse } from '../../application/dto/dtos';

/**
 * Interface du hook use2FA
 */
export interface Use2FAReturn {
  // État
  isLoading: boolean;
  error: string | null;
  qrCodeUrl: string | null;
  secret: string | null;
  backupCodes: string[];

  // Actions - Setup
  generateTOTPSecret: () => Promise<{ qrCode: string; secret: string } | null>;
  sendEmailCode: () => Promise<void>;

  // Actions - Activation/Désactivation
  activate2FA: (methode: 'EMAIL' | 'APPLICATION', code: string, secret?: string) => Promise<string[]>;
  deactivate2FA: (motDePasse: string, code: string) => Promise<void>;

  // Actions - Codes backup
  generateBackupCodes: () => Promise<string[]>;

  // Utilitaires
  clearError: () => void;
  clearData: () => void;
}

/**
 * Hook pour la gestion 2FA
 *
 * @example
 * ```tsx
 * function TwoFactorSetup() {
 *   const { generateTOTPSecret, activate2FA, qrCodeUrl, isLoading } = use2FA();
 *
 *   const handleSetup = async () => {
 *     const result = await generateTOTPSecret();
 *     if (result) {
 *       // Afficher le QR code
 *     }
 *   };
 *
 *   const handleActivate = async (code: string) => {
 *     const backupCodes = await activate2FA('APPLICATION', code, secret);
 *     // Sauvegarder les codes de backup
 *   };
 *
 *   return (
 *     <div>
 *       {qrCodeUrl && <img src={qrCodeUrl} alt="QR Code" />}
 *       //... autre code....//
 *     </div>
 *   );
 * }
 * ```
 */
export function use2FA(): Use2FAReturn {
  const authStore = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  /**
   * Générer un secret TOTP et obtenir le QR code
   */
  const generateTOTPSecret = async (): Promise<{ qrCode: string; secret: string } | null> => {
    setIsLoading(true);
    setError(null);

    const user = authStore.user;
    if (!user) {
      setError('Utilisateur non connecté');
      setIsLoading(false);
      return null;
    }

    try {
      const response = await twoFactorApi.generateSecret({
        idUtilisateur: user.id,
        methode: 'APPLICATION'
      });

      if (!response.succes || !response.data) {
        throw new Error(response.message || 'Impossible de générer le secret TOTP');
      }

      setQrCodeUrl(response.data.qrCodeDataUrl);
      setSecret(response.data.secret);
      setIsLoading(false);

      return {
        qrCode: response.data.qrCodeDataUrl,
        secret: response.data.secret,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de génération TOTP';
      setError(message);
      setIsLoading(false);
      return null;
    }
  };

  /**
   * Envoyer un code de vérification par email
   */
  const sendEmailCode = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);

    const user = authStore.user;
    if (!user) {
      setError('Utilisateur non connecté');
      setIsLoading(false);
      return;
    }

    try {
      const response = await twoFactorApi.sendCode(user.id);

      if (!response.succes) {
        throw new Error(response.message || 'Impossible d\'envoyer le code par email');
      }

      setIsLoading(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur d\'envoi du code';
      setError(message);
      setIsLoading(false);
      throw err;
    }
  };

  /**
   * Activer le 2FA
   */
  const activate2FA = async (
    methode: 'EMAIL' | 'APPLICATION',
    code: string,
    totpSecret?: string
  ): Promise<string[]> => {
    setIsLoading(true);
    setError(null);

    try {
      const codes = await authStore.activate2FA(methode, code, totpSecret);
      setBackupCodes(codes);
      setIsLoading(false);
      return codes;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur d\'activation 2FA';
      setError(message);
      setIsLoading(false);
      throw err;
    }
  };

  /**
   * Désactiver le 2FA
   */
  const deactivate2FA = async (motDePasse: string, code: string): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      await authStore.deactivate2FA(motDePasse, code);
      // Nettoyer les données locales
      setQrCodeUrl(null);
      setSecret(null);
      setBackupCodes([]);
      setIsLoading(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de désactivation 2FA';
      setError(message);
      setIsLoading(false);
      throw err;
    }
  };

  /**
   * Générer de nouveaux codes de backup (remplace les anciens)
   */
  const generateBackupCodes = async (): Promise<string[]> => {
    setIsLoading(true);
    setError(null);

    const user = authStore.user;
    if (!user) {
      setError('Utilisateur non connecté');
      setIsLoading(false);
      return [];
    }

    try {
      const response = await twoFactorApi.generateBackupCodes(user.id);

      if (!response.succes || !response.data) {
        throw new Error(response.message || 'Impossible de générer les codes de backup');
      }

      const codes = response.data.codes;
      setBackupCodes(codes);
      setIsLoading(false);
      return codes;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur de génération des codes backup';
      setError(message);
      setIsLoading(false);
      throw err;
    }
  };

  /**
   * Effacer l'erreur
   */
  const clearError = () => {
    setError(null);
  };

  /**
   * Nettoyer toutes les données 2FA
   */
  const clearData = () => {
    setQrCodeUrl(null);
    setSecret(null);
    setBackupCodes([]);
    setError(null);
  };

  return {
    // État
    isLoading,
    error,
    qrCodeUrl,
    secret,
    backupCodes,

    // Actions - Setup
    generateTOTPSecret,
    sendEmailCode,

    // Actions - Activation/Désactivation
    activate2FA,
    deactivate2FA,

    // Actions - Codes backup
    generateBackupCodes,

    // Utilitaires
    clearError,
    clearData,
  };
}

/**
 * Hook pour obtenir l'état 2FA de l'utilisateur
 * Version optimisée qui ne déclenche pas de re-render inutiles
 *
 * @example
 * ```tsx
 * function SecuritySettings() {
 *   const is2FAActive = use2FAStatus();
 *
 *   return (
 *     <div>
 *       2FA : {is2FAActive ? 'Activé' : 'Désactivé'}
 *     </div>
 *   );
 * }
 * ```
 */
export function use2FAStatus(): boolean {
  const user = useAuthStore((state) => state.user);
  return user?.deuxFacteursActive ?? false;
}

/**
 * Hook pour obtenir la méthode 2FA de l'utilisateur
 * Version optimisée qui ne déclenche pas de re-render inutiles
 *
 * @example
 * ```tsx
 * function SecurityInfo() {
 *   const method = use2FAMethod();
 *
 *   return (
 *     <div>
 *       Méthode 2FA : {method || 'Aucune'}
 *     </div>
 *   );
 * }
 * ```
 */
export function use2FAMethod(): 'EMAIL' | 'APPLICATION' | null {
  const user = useAuthStore((state) => state.user);
  return user?.deuxFacteursMethode ?? null;
}
